import {useCallback} from 'react'
import {getRandomId} from '../helpers'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'
import {convertMarkdownToPlainText} from '../utils/chatUtils'

const useMessageProcessor = (
  // State values
  {messages, messageInputRef},
  // State setters
  {setMessages, setShowCopyToClipboard},
  // Functions from other hooks
  {showChatLoader, handleAutoScrollToBottom, handleQuerySubmit, clearMessageInput}
) => {
  const removePrompts = useCallback(() => {
    let index = messages.findIndex((obj) => obj.isPrompt)
    while (index >= 0) {
      messages.splice(index, 1)
      index = messages.findIndex((obj) => obj.isPrompt)
    }
  }, [messages])

  const handlePromptClick = useCallback(
    (msgObj) => {
      if (msgObj.isPrompt) {
        removePrompts()
        const updatedMessages = [
          ...messages,
          {
            id: getRandomId(),
            message: msgObj.message,
            type: 'User',
            label: 'You'
          }
        ]
        setMessages([...updatedMessages])
        showChatLoader()
        handleAutoScrollToBottom()
        handleQuerySubmit(msgObj.message)
        AnalyticsHandler.onChatbotButtonClick({
          button: {
            name: 'Prompt'
          }
        })
      }
    },
    [messages, setMessages, removePrompts, showChatLoader, handleAutoScrollToBottom, handleQuerySubmit]
  )

  const handleSend = useCallback(() => {
    const messageInput = messageInputRef.current?.value?.trim()
    if (messageInput?.trim() !== '') {
      showChatLoader()
      removePrompts()
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: getRandomId(),
          message: messageInput,
          type: 'User',
          label: 'You'
        }
      ])
      handleAutoScrollToBottom()
      handleQuerySubmit(messageInput)
      clearMessageInput()
      AnalyticsHandler.onChatbotButtonClick({
        button: {
          name: 'Query'
        }
      })
    }
  }, [
    messageInputRef,
    showChatLoader,
    removePrompts,
    setMessages,
    handleAutoScrollToBottom,
    handleQuerySubmit,
    clearMessageInput
  ])

  const handleCopy = useCallback(
    (data) => {
      setShowCopyToClipboard(true)
      const plainText = convertMarkdownToPlainText(data)
      navigator.clipboard.writeText(plainText)
    },
    [setShowCopyToClipboard]
  )

  const handleUserMessageKeyDown = useCallback(
    (event, msgObj) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handlePromptClick(msgObj)
      }
    },
    [handlePromptClick]
  )

  return {
    removePrompts,
    handlePromptClick,
    handleSend,
    handleCopy,
    handleUserMessageKeyDown
  }
}

export default useMessageProcessor

/*
CHATBOT MESSAGE PROCESSOR HOOK

KEY FUNCTIONS:
- removePrompts: Removes all guiding prompt messages from conversation
- handlePromptClick: Processes prompt interactions and initiates queries
- handleSend: Validates and sends user messages through query handler
- handleCopy: Copies text to clipboard with success notification
- handleUserMessageKeyDown: Manages keyboard navigation for user messages

FEATURES:
- Prompt message management and removal
- User input validation and processing
- Clipboard functionality with notifications
- Keyboard navigation support
- Analytics tracking for user interactions
*/
